import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { getImpersonationToken, getImpersonationExpiresAt, removeImpersonationToken } from '../../utils/api';
import '../../styles/admin.css';

const formatCountdown = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const part = token.split('.')[1] || '';
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 2) b64 += '==';
    else if (pad === 3) b64 += '=';
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
};

const ImpersonationBanner: React.FC = () => {
  const token = getImpersonationToken();
  const expiresAt = getImpersonationExpiresAt();

  const [remaining, setRemaining] = useState<number | null>(() => {
    if (!expiresAt) return null;
    return Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
  });

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const r = Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
      setRemaining(r);
      if (r <= 0) {
        clearInterval(timer);
        removeImpersonationToken();
        window.location.href = '/admin/users';
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!token) return null;

  const payload = decodeJwtPayload(token);
  const targetEmail = payload?.email || 'another user';

  const handleExit = () => {
    removeImpersonationToken();
    window.location.href = '/admin/users';
  };

  return (
    <div className="impersonation-banner">
      <div className="impersonation-banner__info">
        <Eye size={14} />
        Viewing as <strong>{targetEmail}</strong> — all actions affect this account
        {remaining !== null && (
          <span className="impersonation-banner__timer">
            {formatCountdown(remaining)}
          </span>
        )}
      </div>
      <button className="impersonation-banner__exit" onClick={handleExit}>
        Exit impersonation
      </button>
    </div>
  );
};

export default ImpersonationBanner;
